import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

interface ModalState {
  id: string;
  priority: number;
  onClose: () => void;
}

interface ModalContextType {
  registerModal: (id: string, onClose: () => void, priority?: number) => void;
  unregisterModal: (id: string) => void;
  hasOpenModals: () => boolean;
  closeTopModal: () => boolean;
  getTopModal: () => ModalState | null;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export function ModalProvider({ children }: { children: React.ReactNode }) {
  const [modals, setModals] = useState<ModalState[]>([]);
  // Use a ref to track modals for stable callback references
  const modalsRef = useRef<ModalState[]>([]);
  modalsRef.current = modals;

  const registerModal = useCallback((id: string, onClose: () => void, priority: number = 0) => {
    setModals(prev => {
      const filtered = prev.filter(m => m.id !== id);
      return [...filtered, { id, onClose, priority }].sort((a, b) => b.priority - a.priority);
    });
  }, []);

  const unregisterModal = useCallback((id: string) => {
    setModals(prev => prev.filter(m => m.id !== id));
  }, []);

  // Stable reference — reads from ref, never causes re-render dependency changes
  const hasOpenModals = useCallback(() => {
    return modalsRef.current.length > 0;
  }, []);

  const closeTopModal = useCallback(() => {
    const current = modalsRef.current;
    if (current.length === 0) return false;
    current[0].onClose();
    return true;
  }, []);

  const getTopModal = useCallback(() => {
    const current = modalsRef.current;
    return current.length > 0 ? current[0] : null;
  }, []);

  return (
    <ModalContext.Provider
      value={{
        registerModal,
        unregisterModal,
        hasOpenModals,
        closeTopModal,
        getTopModal,
      }}
    >
      {children}
    </ModalContext.Provider>
  );
}

export function useModal() {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
}
